import Add from "@mui/icons-material/Add";
import { Box, Button } from "@mui/material";
import {
  GridColDef,
  GridPinnedRowsProp,
  GridRowOrderChangeParams,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import React, { useMemo } from "react";

import { SubmissionFormDatagrid } from "./SubmissionFormDatagrid";

export interface AddButtonConfig {
  /** Text to display on the add button */
  buttonText: string;
  /** Icon to display on the add button (defaults to Add icon) */
  icon?: React.ReactNode;
  /** Number of columns the add button should span when no data rows exist */
  colSpan?: number;
}

// Define a special row type that includes the add button properties
type AddButtonRow = {
  id: "add-button";
  isAddButtonRow: true;
  [key: string]: unknown; // Allow any additional properties to match T
};

// Helper to check if a row is an add button row
export const isAddButtonRow = (row: { isAddButtonRow?: boolean }): row is AddButtonRow => {
  return row.isAddButtonRow === true;
};

interface DataGridWithAddButtonProps<T extends GridValidRowModel & { id: string | number }> {
  /** The actual data rows */
  rows: T[];
  /** Column definitions for the data rows */
  columns: GridColDef<T>[];
  /** Configuration for the add button */
  addButtonConfig: AddButtonConfig;
  /** Callback when add button is clicked */
  onAdd: () => void;
  /** Factory function to create an empty row for the add button */
  createEmptyRow: () => T;
  /** Other props to pass to SubmissionFormDatagrid */
  gridProps?: {
    onRowClick?: () => void;
    rowReordering?: boolean;
    onRowOrderChange?: (params: any) => void;
    noRowsLabel?: string;
    showClickableCursor?: boolean;
  };
  loading?: boolean;
  rowReordering?: boolean;
  pinnedRows?: GridPinnedRowsProp<T>;
  onRowOrderChange?: (params: GridRowOrderChangeParams) => void;
}

export function DataGridWithAddButton<T extends GridValidRowModel & { id: string | number }>({
  rows,
  columns,
  addButtonConfig,
  onAdd,
  createEmptyRow,
  gridProps,
  loading = false,
  rowReordering = false,
  pinnedRows,
  onRowOrderChange,
}: DataGridWithAddButtonProps<T>) {
  // Add isAddButtonRow property to all rows
  const enhancedRows = useMemo(() => {
    if (rows.length === 0) {
      // Create a proper empty row using the factory function
      const emptyRow = createEmptyRow();
      return [{ ...emptyRow, id: "add-button", isAddButtonRow: true }];
    }
    return rows.map((row) => ({ ...row, isAddButtonRow: false }));
  }, [rows, createEmptyRow]);

  // Enhance columns to handle add button row
  const enhancedColumns = useMemo(() => {
    return columns.map((column, index) => ({
      ...column,
      // For first column when no data rows, handle add button rendering and colspan
      ...(index === 0 &&
        rows.length === 0 && {
          colSpan: addButtonConfig.colSpan || columns.length,
          renderCell: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <Button
                startIcon={addButtonConfig.icon || <Add />}
                onClick={onAdd}
                variant="text"
                color="primary"
                sx={{ width: "100%", justifyContent: "flex-start" }}
              >
                {addButtonConfig.buttonText}
              </Button>
            </Box>
          ),
        }),
      // For other columns when no data rows, hide content
      ...(index > 0 &&
        rows.length === 0 && {
          renderCell: () => null,
        }),
    }));
  }, [columns, rows.length, addButtonConfig, onAdd]);

  return (
    <SubmissionFormDatagrid
      rows={enhancedRows}
      columns={enhancedColumns}
      rowReordering={rowReordering}
      pinnedRows={pinnedRows}
      onRowOrderChange={onRowOrderChange}
      onRowClick={rows.length === 0 ? onAdd : gridProps?.onRowClick}
      showClickableCursor={rows.length === 0 || !!gridProps?.onRowClick}
      loading={loading}
      {...(gridProps || {})}
    />
  );
}
